import React, { useMemo } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import styles from "../stylesER.module.scss";

const MESES = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];

const formatCompact = (value) => {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return value.toFixed(0);
};

/* ── Tooltip ── */
const CustomTooltip = ({ active, payload, label, formatCurrency }) => {
  if (!active || !payload?.length) return null;

  const d = payload[0]?.payload || {};
  if (d.fact === null) return null;

  const fmt = (v) => (typeof formatCurrency === "function" ? formatCurrency(v) : formatCompact(v));

  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip-title">{label}</p>
      <p className="chart-tooltip-row" style={{ color: "#0c0c58" }}>
        <span className="chart-tooltip-dot" style={{ background: "#0c0c58" }} />
        Facturación: {fmt(d.fact)}
      </p>
      <p className="chart-tooltip-row" style={{ color: "#0c0c58" }}>
        <span className="chart-tooltip-dot" style={{ background: "#0c0c58" }} />
        Costos Variables: {fmt(d.cv)}
      </p>
      <p className="chart-tooltip-row" style={{ color: "#d4760a" }}>
        <span className="chart-tooltip-dot" style={{ background: "#d4760a" }} />
        Costos Fijos: {fmt(d.cf)}
      </p>
      <p className="chart-tooltip-row" style={{ color: d.ebitda >= 0 ? "#2d9e5e" : "#c0392b" }}>
        <span className="chart-tooltip-dot" style={{ background: d.ebitda >= 0 ? "#2d9e5e" : "#c0392b" }} />
        EBITDA: {fmt(d.ebitda)}
      </p>
      {d.ebitdaAcum !== null && d.ebitdaAcum !== undefined && (
        <p className="chart-tooltip-row" style={{ color: "#8e44ad" }}>
          <span className="chart-tooltip-dot" style={{ background: "#8e44ad" }} />
          EBITDA Acumulado: {fmt(d.ebitdaAcum)}
        </p>
      )}
    </div>
  );
};

/* ── Modal ── */
export const ERChartModal = ({
  open,
  onClose,
  facturacionMensual,
  costosVariablesMensual,
  costosFijosMensual,
  ebitdaMensual,
  formatCurrency,
  selectedYear,
  verUltimos12Meses,
}) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonthIndex = now.getMonth();
  const showHoy = selectedYear === currentYear && !verUltimos12Meses;

  const chartData = useMemo(() => {
    let acum = 0;
    return MESES.map((mes, i) => {
      const cf = costosFijosMensual?.[i] || 0;
      const cv = costosVariablesMensual?.[i] || 0;
      const fact = facturacionMensual?.[i] || 0;
      const ebitda = ebitdaMensual?.[i] || 0;
      const hasData = fact !== 0 || cf !== 0 || cv !== 0;

      if (hasData) acum += ebitda;

      // Stack: ebitdaNeg → spacer → ebitdaPos → cf → cv
      // Cuando EBITDA >= 0: 0 + 0 + ebitda + cf + cv (todo arriba del eje X)
      // Cuando EBITDA < 0: ebitda(abajo) + spacer(invisible vuelve a 0) + 0 + cf + cv (costos arriba)
      return {
        mes,
        fact: hasData ? fact : null,
        cv: hasData ? cv : null,
        cf: hasData ? cf : null,
        ebitda: hasData ? ebitda : null,
        ebitdaNeg: hasData ? Math.min(ebitda, 0) : null,
        spacer: hasData && ebitda < 0 ? -ebitda : 0,
        ebitdaPos: hasData ? Math.max(ebitda, 0) : null,
        ebitdaAcum: hasData ? acum : null,
      };
    });
  }, [facturacionMensual, costosFijosMensual, costosVariablesMensual, ebitdaMensual]);

  // Promedios para líneas de tendencia horizontal
  const averages = useMemo(() => {
    const valid = chartData.filter((d) => d.fact !== null);
    if (valid.length < 2) return null;
    const n = valid.length;
    return {
      ebitda: valid.reduce((s, d) => s + (d.ebitda || 0), 0) / n,
      fact: valid.reduce((s, d) => s + d.fact, 0) / n,
    };
  }, [chartData]);

  const formatForTooltip = (value) => {
    if (typeof formatCurrency === "function") return formatCurrency(value);
    return formatCompact(value);
  };

  if (!open) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={styles.modalContent}
        onClick={(e) => e.stopPropagation()}
        style={{ width: "85vw", maxWidth: 1350 }}
      >
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.6rem",
              cursor: "pointer",
              color: "#999",
              lineHeight: 1,
              padding: "0 4px",
            }}
          >
            &times;
          </button>
        </div>

        {/* Gráfico */}
        <div style={{ width: "100%", height: "60vh", minHeight: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 20, right: 20, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
              <XAxis
                dataKey="mes"
                tick={{ fontSize: 12, fill: "#666" }}
                axisLine={{ stroke: "#ddd" }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#999" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatCompact}
                width={55}
                domain={["dataMin", "auto"]}
              />
              <Tooltip
                content={<CustomTooltip formatCurrency={formatForTooltip} />}
                cursor={{ fill: "rgba(0,0,0,0.04)" }}
              />

              <ReferenceLine y={0} stroke="#bbb" strokeWidth={1} />

              {showHoy && (
                <ReferenceLine
                  x={MESES[currentMonthIndex]}
                  stroke="#c57b1a"
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  label={{
                    value: "Hoy",
                    position: "insideTopRight",
                    fill: "#c57b1a",
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                />
              )}

              {/* Líneas de tendencia horizontal */}
              {averages && (
                <>
                  <ReferenceLine
                    y={averages.fact}
                    stroke="#555"
                    strokeDasharray="4 2"
                    strokeWidth={1}
                    strokeOpacity={0.5}
                  />
                  <ReferenceLine
                    y={averages.ebitda}
                    stroke="#2d9e5e"
                    strokeDasharray="4 2"
                    strokeWidth={1}
                    strokeOpacity={0.5}
                  />
                </>
              )}

              {/* Stack: ebitdaNeg → spacer (invisible) → cf → cv → ebitdaPos */}
              <Bar dataKey="ebitdaNeg" stackId="er" fill="#c0392b" barSize={32} />
              <Bar dataKey="spacer" stackId="er" fill="transparent" />
              <Bar dataKey="cf" stackId="er" fill="#d4760a" />
              <Bar dataKey="cv" stackId="er" fill="#0c0c58" />
              <Bar dataKey="ebitdaPos" stackId="er" fill="#2d9e5e" radius={[3, 3, 0, 0]} />

              {/* EBITDA Acumulado — línea púrpura */}
              <Line
                dataKey="ebitdaAcum"
                stroke="#8e44ad"
                strokeWidth={2.5}
                dot={{ r: 3, fill: "#8e44ad" }}
                activeDot={{ r: 5 }}
                type="monotone"
                strokeDasharray="6 3"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Leyenda */}
        <div className="chart-legend" style={{ marginTop: 8 }}>
          <div className="chart-legend-item">
            <span className="chart-legend-bar" style={{ background: "#0c0c58" }} />
            Costos Variables
          </div>
          <div className="chart-legend-item">
            <span className="chart-legend-bar" style={{ background: "#d4760a" }} />
            Costos Fijos
          </div>
          <div className="chart-legend-item">
            <span className="chart-legend-bar" style={{ background: "#2d9e5e" }} />
            EBITDA
          </div>
          <div className="chart-legend-item">
            <span className="chart-legend-line" style={{ borderColor: "#8e44ad", borderStyle: "dashed" }} />
            EBITDA Acumulado
          </div>
        </div>
      </div>
    </div>
  );
};
